import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Seo from '@/components/Seo';
import {
  Truck, MapPin, Calendar, Phone, Mail, ShieldCheck,
  CheckCircle2, ArrowRight, Star, Package, Boxes, Sparkles
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CITIES = ['Mumbai', 'Pune', 'Bengaluru', 'Hyderabad', 'Delhi', 'Gurgaon', 'Noida', 'Chennai', 'Kolkata', 'Ahmedabad', 'Indore', 'Bhopal', 'Lucknow', 'Jaipur', 'Chandigarh', 'Coimbatore', 'Bhubaneswar', 'Surat', 'Vadodara', 'Nashik', 'Nagpur', 'Visakhapatnam', 'Kochi', 'Goa'];

const PackersMoversPage = () => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({ house_sizes: [], tiers: [] });
  const [quote, setQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);

  const [form, setForm] = useState({
    from_city: 'Mumbai',
    to_city: 'Pune',
    size_id: '2bhk',
    tier_id: 'standard',
    move_date: '',
    from_address: '',
    to_address: '',
    name: '',
    phone: '',
    whatsapp: '',
    email: '',
    special_items: '',
  });

  useEffect(() => {
    fetch(`${API}/movers/config`).then(r => r.json()).then((d) => setConfig(d));
  }, []);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const getQuote = async () => {
    setLoadingQuote(true);
    try {
      const res = await fetch(`${API}/movers/quote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_city: form.from_city, to_city: form.to_city, size_id: form.size_id, tier_id: form.tier_id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Quote failed');
      setQuote(data);
    } catch (e) { toast.error(e.message); }
    finally { setLoadingQuote(false); }
  };

  const submitLead = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) { toast.error('Name & phone are required'); return; }
    if (!form.move_date) { toast.error('Please pick a move date'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        from_city: form.from_city,
        to_city: form.to_city,
        size_id: form.size_id,
        tier_id: form.tier_id,
        move_date: form.move_date,
        from_address: form.from_address || undefined,
        to_address: form.to_address || undefined,
        name: form.name,
        phone: form.phone,
        whatsapp: form.whatsapp || undefined,
        email: form.email || undefined,
        special_items: form.special_items || undefined,
        estimate_low: quote?.quote?.estimate_low,
        estimate_high: quote?.quote?.estimate_high,
      };
      const res = await fetch(`${API}/movers/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Submission failed');
      toast.success(data.message);
      navigate(`/services/booking-success?bid=${encodeURIComponent(data.lead_id)}&type=movers`);
    } catch (e) { toast.error(e.message); }
    finally { setSubmitting(false); }
  };

  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="packers-movers-page">
      <Seo
        title="Packers & Movers in 24+ Cities · Get Instant Quote"
        description="Trusted Packers & Movers across India. Local & intercity. Get an instant transparent quote in 30 seconds. Insurance included, 4.8★ rated."
        path="/packers-movers"
      />

      {/* HERO */}
      <section className="relative pt-36 pb-12 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E293B] to-[#0F172A] text-white">
        <div className="absolute inset-0 opacity-15">
          <img src="https://images.unsplash.com/photo-1600518464441-9154a4dea21b?auto=format&fit=crop&w=1600&q=80" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="container-luxury relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/15 text-[#D4AF37] rounded-full text-sm font-medium mb-5 border border-[#D4AF37]/30">
                <Truck className="h-4 w-4" /> Packers &amp; Movers
              </span>
              <h1 className="font-['Plus_Jakarta_Sans'] text-5xl md:text-7xl font-bold leading-[1.05] mb-4">
                Moving home? <span className="text-[#D4AF37]">Get a quote in 30 seconds.</span>
              </h1>
              <p className="text-white/75 text-lg max-w-2xl mb-6">
                Local + intercity moves. Transparent pricing. Free packing material. Up to ₹15 lakh insurance.
                Servicing 24+ Indian cities.
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                {[
                  { k: '24+', l: 'Cities' },
                  { k: '40K+', l: 'Moves done' },
                  { k: '4.8★', l: 'Avg rating' },
                  { k: '₹15L', l: 'Insurance' },
                ].map((s) => (
                  <div key={s.l}>
                    <p className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#D4AF37]">{s.k}</p>
                    <p className="text-xs text-white/65 uppercase tracking-wider">{s.l}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* QUOTE CARD */}
            <div className="lg:col-span-5 bg-white rounded-sm p-6 text-[#0F172A] shadow-2xl" data-testid="mover-quote-form">
              <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold mb-4">Instant Quote Calculator</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">From City</Label>
                  <select className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" value={form.from_city} onChange={(e) => upd('from_city', e.target.value)} data-testid="from-city-select">
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">To City</Label>
                  <select className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" value={form.to_city} onChange={(e) => upd('to_city', e.target.value)} data-testid="to-city-select">
                    {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">Home Size</Label>
                  <select className="mt-1 w-full h-10 rounded-sm border border-input bg-background px-3 text-sm" value={form.size_id} onChange={(e) => upd('size_id', e.target.value)} data-testid="size-select">
                    {config.house_sizes.map((s) => <option key={s.size_id} value={s.size_id}>{s.label}</option>)}
                  </select>
                </div>
              </div>
              <Button onClick={getQuote} disabled={loadingQuote} className="w-full mt-4 bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold py-5" data-testid="get-quote-btn">
                {loadingQuote ? 'Calculating...' : 'Get My Quote'} <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="text-xs text-muted-foreground mt-2 text-center">No commitment · No spam</p>
            </div>
          </div>
        </div>
      </section>

      {/* QUOTE RESULT */}
      {quote && (
        <section className="py-12 bg-white" data-testid="quote-result">
          <div className="container-luxury">
            <div className="text-center mb-8">
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">Your Estimate</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl font-bold text-[#0F172A]">
                {quote.quote.from_city} → {quote.quote.to_city}
              </h2>
              <p className="text-muted-foreground mt-2">{quote.quote.size_label} · approx {quote.quote.distance_km_estimate} km</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
              {quote.all_tiers.map((t) => {
                const active = t.tier_id === form.tier_id;
                return (
                  <button
                    key={t.tier_id}
                    onClick={() => upd('tier_id', t.tier_id)}
                    className={`text-left bg-white rounded-sm border-2 p-6 transition-all ${active ? 'border-[#D4AF37] shadow-lg scale-[1.02]' : 'border-border/50 hover:border-[#D4AF37]/40'}`}
                    data-testid={`tier-${t.tier_id}`}
                  >
                    {t.tier_id === 'standard' && <span className="inline-block px-2 py-1 bg-[#D4AF37] text-[#0F172A] text-[10px] font-bold rounded-full uppercase mb-2">Most Popular</span>}
                    <h3 className="font-['Plus_Jakarta_Sans'] text-2xl font-bold text-[#0F172A] mb-1">{t.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{t.tagline}</p>
                    <div className="mb-4">
                      <p className="text-xs text-muted-foreground">Pricing</p>
                      <p className="font-semibold text-[#0F172A] text-sm">Quote shared after free pre-move survey</p>
                    </div>
                    <ul className="space-y-1.5 text-sm">
                      {t.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#10B981] flex-shrink-0 mt-0.5" /> <span className="text-muted-foreground">{f}</span></li>
                      ))}
                    </ul>
                  </button>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <Button onClick={() => { setShowLeadForm(true); setTimeout(() => document.getElementById('lead-form')?.scrollIntoView({ behavior: 'smooth' }), 100); }} className="bg-[#0F172A] hover:bg-[#1E293B] text-white px-7 py-6" data-testid="confirm-quote-btn">
                Confirm This Quote &amp; Book a Move Manager Call <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
              <p className="text-xs text-muted-foreground mt-3">Quote is indicative. Final price confirmed after a free pre-move survey (in-person or video).</p>
            </div>
          </div>
        </section>
      )}

      {/* LEAD FORM */}
      {showLeadForm && quote && (
        <section id="lead-form" className="py-16 bg-[#F9F9F7]">
          <div className="container-luxury max-w-3xl">
            <div className="bg-white p-8 rounded-sm border border-border/50">
              <h2 className="font-['Plus_Jakarta_Sans'] text-3xl font-bold text-[#0F172A] mb-2">Tell us a bit more</h2>
              <p className="text-muted-foreground text-sm mb-6">Our move-manager will WhatsApp/call within 2 working hours with a firm price.</p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Full Name *</Label>
                  <Input className="mt-1.5" value={form.name} onChange={(e) => upd('name', e.target.value)} data-testid="lead-name" />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input className="mt-1.5" value={form.phone} onChange={(e) => upd('phone', e.target.value)} placeholder="+91 ..." data-testid="lead-phone" />
                </div>
                <div>
                  <Label>WhatsApp</Label>
                  <Input className="mt-1.5" value={form.whatsapp} onChange={(e) => upd('whatsapp', e.target.value)} placeholder="Same as phone if blank" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input type="email" className="mt-1.5" value={form.email} onChange={(e) => upd('email', e.target.value)} />
                </div>
                <div>
                  <Label>Move Date *</Label>
                  <Input type="date" min={minDate} className="mt-1.5" value={form.move_date} onChange={(e) => upd('move_date', e.target.value)} data-testid="lead-date" />
                </div>
                <div className="md:col-span-2">
                  <Label>From Address (optional)</Label>
                  <Input className="mt-1.5" value={form.from_address} onChange={(e) => upd('from_address', e.target.value)} placeholder="Full pickup address" />
                </div>
                <div className="md:col-span-2">
                  <Label>To Address (optional)</Label>
                  <Input className="mt-1.5" value={form.to_address} onChange={(e) => upd('to_address', e.target.value)} placeholder="Full drop address" />
                </div>
                <div className="md:col-span-2">
                  <Label>Special items (piano, gym, fish tank, etc.)</Label>
                  <Input className="mt-1.5" value={form.special_items} onChange={(e) => upd('special_items', e.target.value)} />
                </div>
              </div>

              <Button onClick={submitLead} disabled={submitting} className="w-full mt-6 bg-[#D4AF37] hover:bg-[#B8932F] text-[#0F172A] font-bold py-6" data-testid="submit-lead-btn">
                {submitting ? 'Submitting...' : 'Lock in this quote'}
              </Button>
              <p className="text-xs text-muted-foreground mt-3 text-center">By submitting, you agree to our <Link to="/terms" className="text-[#D4AF37] hover:underline">Terms</Link>. No spam, ever.</p>
            </div>
          </div>
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="py-20 bg-white">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">How it works</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">From quote to keys, in 4 steps.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
            {[
              { i: Sparkles, h: 'Instant Quote', b: 'Tell us where & how much — get an estimate range immediately.' },
              { i: MapPin, h: 'Free Pre-Move Survey', b: 'Move manager visits / video calls to lock the final price.' },
              { i: Package, h: 'Pro Packing & Move', b: 'Trained crew arrives. Pack, load, transport, deliver.' },
              { i: ShieldCheck, h: 'Unpack & Insured', b: 'Re-assemble and unbox at destination. Insured end-to-end.' },
            ].map((s, i) => (
              <div key={i} className="bg-[#F9F9F7] p-6 rounded-sm">
                <div className="h-11 w-11 rounded-sm bg-[#0F172A] text-[#D4AF37] flex items-center justify-center mb-4">
                  <s.i className="h-5 w-5" />
                </div>
                <h4 className="font-semibold text-[#0F172A] mb-1">{i + 1}. {s.h}</h4>
                <p className="text-sm text-muted-foreground">{s.b}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INCLUDED */}
      <section className="py-20 bg-[#F9F9F7]">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">What's included</p>
              <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A] mb-4">Everything you'd expect — and a little more.</h2>
              <ul className="space-y-3 mt-6">
                {[
                  'Free pre-move survey (in-person or video)',
                  'Professional packing material (bubble wrap, corrugation, cartons, tape)',
                  'Disassembly & re-assembly of standard furniture',
                  'Loading, transit, unloading & arranging at destination',
                  'Transit insurance up to ₹15 lakh (Premium tier)',
                  'GPS-tracked truck — share live link with family',
                  '24/7 helpline on move day',
                  '7-day claim window for any damage',
                ].map((p, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-[#10B981] flex-shrink-0 mt-0.5" /> <span className="text-muted-foreground">{p}</span></li>
                ))}
              </ul>
            </div>
            <div className="relative aspect-square rounded-sm overflow-hidden">
              <img src="https://images.unsplash.com/photo-1600518464441-9154a4dea21b?auto=format&fit=crop&w=1200&q=80" alt="Packers movers" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-white">
        <div className="container-luxury">
          <div className="text-center mb-12">
            <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-[0.2em] mb-2">What customers say</p>
            <h2 className="font-['Plus_Jakarta_Sans'] text-4xl md:text-5xl font-bold text-[#0F172A]">40,000+ moves, zero broken cricket bats.</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: 'Aanya Sharma', c: 'Mumbai → Pune · 2BHK', r: 5, q: 'Best money spent. They packed in 3 hours and unpacked at the new place in 2. My mom called the supervisor "beta" by the end.' },
              { n: 'Rahul Singh', c: 'Bengaluru → Hyderabad · 3BHK', r: 5, q: 'Survey was video-only and the final price was within ₹600 of the quote. Premium tier was worth every rupee.' },
              { n: 'Priya Iyer', c: 'Within Chennai · 1BHK', r: 4.8, q: 'Affordable and on time. Wished they had bubble-wrapped my plants too, but otherwise A+.' },
            ].map((t, i) => (
              <div key={i} className="bg-[#F9F9F7] p-6 rounded-sm">
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <Star key={j} className={`h-4 w-4 ${j < Math.round(t.r) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-gray-300'}`} />
                  ))}
                </div>
                <p className="text-[#0F172A] leading-relaxed mb-4 italic">"{t.q}"</p>
                <p className="font-semibold text-[#0F172A] text-sm">{t.n}</p>
                <p className="text-xs text-muted-foreground">{t.c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackersMoversPage;
